import { MapViewportEventView, SummaryCategoryEnumType } from '@mark43/cad-api';
import { ESRI_DEFAULT_ICON_LABEL_FONT } from '../configurations';
import type { LabelClassProperties } from '../types';
import { isValidLatLng } from '../../location/helpers';
import { initOffsetCalculator } from './lat-lng';
import { getEventLocation } from './locations';
import { colors } from '../../colors/colors';

// Assigned
import assignedInactive from '../images/event-icons/assigned/event_assigned_inactive.svg';
import assignedSelected from '../images/event-icons/assigned/event_assigned_selected.svg';
// Unassigned
import unassignedInactive from '../images/event-icons/unassigned/event_unassigned_inactive.svg';
import unassignedSelected from '../images/event-icons/unassigned/event_unassigned_selected.svg';

export const EVENT_ICON_SIZE = '32px';

export const eventUniqueValueInfos = [
    // Assigned
    {
        value: 'Assigned, Inactive',
        symbol: {
            type: 'picture-marker',
            url: assignedInactive,
            width: EVENT_ICON_SIZE,
            height: EVENT_ICON_SIZE,
        },
    },
    {
        value: 'Assigned, Selected',
        symbol: {
            type: 'picture-marker',
            url: assignedSelected,
            width: EVENT_ICON_SIZE,
            height: EVENT_ICON_SIZE,
        },
    },
    // Unassigned
    {
        value: 'Unassigned, Inactive',
        symbol: {
            type: 'picture-marker',
            url: unassignedInactive,
            width: EVENT_ICON_SIZE,
            height: EVENT_ICON_SIZE,
        },
    },
    {
        value: 'Unassigned, Selected',
        symbol: {
            type: 'picture-marker',
            url: unassignedSelected,
            width: EVENT_ICON_SIZE,
            height: EVENT_ICON_SIZE,
        },
    },
];

export const eventIconRenderer = {
    type: 'unique-value', // autocasts as new UniqueValueRenderer()
    field: 'iconName',
    defaultSymbol: {
        // used when item field dont match anything defined in uniqueValueInfos
        type: 'simple-marker',
        style: 'square',
        color: colors.mapLabelDefault,
        size: '32px',
    },
    uniqueValueInfos: eventUniqueValueInfos,
    authoringInfo: undefined,
};

/** This function maps the different events to one of the 4 unique icon types we will render */
export const getEventIconType = (summaryCategory: SummaryCategoryEnumType) => {
    if (
        summaryCategory === 'EVENT_ASSIGNED' ||
        summaryCategory === 'FIRE_EVENT_ASSIGNED' ||
        summaryCategory === 'EMS_EVENT_ASSIGNED'
    ) {
        return 'Assigned';
    } else {
        return 'Unassigned';
    }
};

const eventIconLabel: LabelClassProperties = {
    symbol: {
        type: 'text',
        color: colors.mapLabelText,
        font: {
            ...ESRI_DEFAULT_ICON_LABEL_FONT,
            size: '10px',
        },
    },
    labelPlacement: 'center-center',
    labelExpressionInfo: {
        expression: '$feature.priority',
    },
    maxScale: 0,
    minScale: 25000000,
};

export const eventIconLabels = [eventIconLabel];

export function adjustMapviewportEventsLocations(
    zoom: number,
    mapViewportEvents: MapViewportEventView[],
    selectedEventId?: number
): MapViewportEventView[] {
    // filter out mapViewportEvents that don't won't show on the map
    if (!mapViewportEvents?.length) {
        return [];
    }
    const calculateEventOffsetLatLng = initOffsetCalculator(zoom);
    const adjustedEvents = [];

    let idOfSelectedEvent: number | undefined;
    let idOfEventToReplaceSelected: number | undefined;

    for (let i = 0; i < mapViewportEvents.length; i++) {
        const mapEvent = mapViewportEvents[i];
        const eventLocation = getEventLocation(mapEvent);
        if (
            mapEvent?.visible &&
            eventLocation &&
            isValidLatLng(eventLocation.latitude, eventLocation.longitude)
        ) {
            const isSelected = mapEvent.entityId === selectedEventId;
            // need to offset position of events if they have the same lat/lng, otherwise they would sit on top of each other
            const { adjustedLatLng, eventIdToReplaceGeoLocation } = calculateEventOffsetLatLng(
                eventLocation.latitude!,
                eventLocation.longitude!,
                mapEvent.entityId,
                isSelected
            );
            if (eventIdToReplaceGeoLocation) {
                idOfEventToReplaceSelected = eventIdToReplaceGeoLocation;
            }
            if (isSelected) {
                idOfSelectedEvent = mapEvent.entityId;
            }
            const adjustedEventLocation = {
                ...eventLocation,
                latitude: adjustedLatLng.latitude,
                longitude: adjustedLatLng.longitude,
            };
            adjustedEvents.push({
                ...mapEvent,
                geoLocation: adjustedEventLocation,
            });
        }
    }

    const totalLength = adjustedEvents.length;
    if (idOfSelectedEvent && idOfEventToReplaceSelected) {
        const eventToReplaceSelected = adjustedEvents.find(
            (event) => event.entityId === idOfEventToReplaceSelected
        );
        const selectedEvent = adjustedEvents.find((event) => event.entityId === idOfSelectedEvent);
        const indexOfEventToReplaceSelected = adjustedEvents.findIndex(
            (e) => e.entityId === eventToReplaceSelected?.entityId
        );
        const indexOfSelectedEvent = adjustedEvents.findIndex(
            (e) => e.entityId === selectedEvent?.entityId
        );
        const tempLocation = eventToReplaceSelected?.geoLocation;
        if (
            indexOfEventToReplaceSelected !== undefined &&
            indexOfSelectedEvent !== undefined &&
            indexOfSelectedEvent !== indexOfEventToReplaceSelected &&
            indexOfEventToReplaceSelected >= 0 &&
            indexOfEventToReplaceSelected < totalLength &&
            indexOfSelectedEvent >= 0 &&
            indexOfSelectedEvent < totalLength
        ) {
            adjustedEvents[indexOfEventToReplaceSelected] = {
                ...adjustedEvents[indexOfEventToReplaceSelected],
                geoLocation: adjustedEvents[indexOfSelectedEvent]?.geoLocation,
            };
            adjustedEvents[indexOfSelectedEvent] = {
                ...adjustedEvents[indexOfSelectedEvent],
                geoLocation: tempLocation,
            };
        }
    }
    return adjustedEvents;
}
